import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

/**
 * Default common block data for the Why Estate Planning Matters slide.
 * This agnostic structure can be transformed into Craft.js or any other block editor format.
 */
export function getDefaultWhyEstatePlanningMattersData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'why-estate-planning-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: "Estate planning is a critical aspect of financial management that serves to protect your assets, manage tax liabilities, and orchestrate the smooth transition of your estate to your heirs both during life and after death. While it's natural to avoid thinking about what happens after we pass away, taking the time to plan your estate can provide peace of mind for you and your loved ones. **Planning & implementing various strategies today can not only increase the value of the legacy you leave behind but reduce what is ultimately subject to federal and/or state tax.**",
              },
            },
          ],
        },
      },
      {
        id: 'list-section',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'item-1',
              type: 'PresentationListItem',
              props: {
                title: 'Protecting beneficiaries',
                description:
                  'Estate planning ensures that your assets are distributed according to your wishes. This is particularly important if you have minor children or dependents who rely on your financial support. Through mechanisms like trusts, you can ensure that funds are available to meet their needs, managed by a trustworthy individual until they come of age.',
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-2',
              type: 'PresentationListItem',
              props: {
                title: 'Minimizing taxes',
                description:
                  'Estate planning can help reduce the tax burden on your heirs by utilizing various strategies and instruments such as gifting, charitable donations, and trusts. This is especially important for larger estates that may be subject to federal and state estate taxes. Proper planning can save your beneficiaries a significant amount of money and help preserve the value of your estate.',
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-3',
              type: 'PresentationListItem',
              props: {
                title: 'Avoiding probate',
                description:
                  "Probate is the legal process through which a deceased person's estate is managed and distributed. This process can be time-consuming, costly, and public. Estate planning tools like living trusts can help you avoid probate, ensuring that your assets are distributed more quickly and privately.",
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-4',
              type: 'PresentationListItem',
              props: {
                title: 'Healthcare decisions',
                description:
                  "Estate planning isn't just about wealth; it also includes planning for your healthcare in the event you become incapacitated. Documents like a living will or healthcare power of attorney can dictate your medical preferences and designate someone to make healthcare decisions on your behalf.",
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
            {
              id: 'item-5',
              type: 'PresentationListItem',
              props: {
                title: 'Peace of mind',
                description:
                  "Knowing that your financial affairs are in order gives you and your loved ones peace of mind. It alleviates the stress and potential conflicts that may arise from not having clear instructions, ensuring that your wishes are honored and your family's future is secure.",
                iconType: 'check',
                iconColor: 'PRIMARY',
              },
            },
          ],
        },
      },
    ],
  };
}
